import dayjs from 'dayjs';
import styled from 'styled-components';
import { t } from 'localization/utils/t';
import Link from 'next/link';

import { formatDate } from 'shared/utils/formatDate';
import { formatString } from 'productTemplate/utils/formatStringForUrl';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';

const TP = 'productTemplate.components.windows.FactsWindow';

const releaseMonthRange = (releaseDate: string): string => {
  return `${formatDate.yearMonthDay(dayjs(releaseDate).startOf('month'))}-${formatDate.yearMonthDay(
    dayjs(releaseDate).endOf('month'),
  )}`;
};

export const FactsTable = ({ productTemplate }: { productTemplate: IProductTemplate }) => {
  const { isCategoryAndFacetMatching } = useStoreSelector((state) => state.pdp.factsWindowData);
  const {
    brandName,
    category,
    color,
    designer,
    details,
    featuredIn,
    midsole,
    nickname,
    productCategory,
    productType,
    releaseDate,
    season,
    sku,
    upperMaterial,
  } = productTemplate;
  const isSneakers = productCategory === 'shoes';
  const data = {
    ...(releaseDate && {
      releaseDate: {
        left: t(`${TP}.releaseDate`, 'Release Date'),
        right: (
          <Link
            href={`/search?release_date=${releaseMonthRange(releaseDate)}`}
            prefetch={false}
            data-qa="release_date_link"
          >
            {formatDate.monthDayYearKebab(releaseDate)}
          </Link>
        ),
      },
    }),
    ...(season && { season: { left: t(`${TP}.season`, 'Season'), right: season } }),
    ...(sku && { sku: { left: t(`${TP}.sku`, 'SKU'), right: sku } }),
    ...(nickname && { nickname: { left: t(`${TP}.nickname`, 'Nickname'), right: nickname } }),
    ...(details && { details: { left: t(`${TP}.colorway`, 'Colorway'), right: details } }),
    ...(color && {
      color: {
        left: t(`${TP}.mainColor`, 'Main Color'),
        right: (
          <Link
            href={`/${productType}/color/${formatString(color)}`}
            prefetch={false}
            data-qa="color_link"
          >
            {color}
          </Link>
        ),
      },
    }),
    ...(upperMaterial && {
      upperMaterial: {
        left: t(`${TP}.upperMaterial`, 'Upper Material'),
        right: (
          <Link
            href={`/sneakers/upper-material/${formatString(upperMaterial)}`}
            prefetch={false}
            data-qa="upper_material_link"
          >
            {upperMaterial}
          </Link>
        ),
      },
    }),
    ...(midsole && {
      midsole: {
        left: t(`${TP}.technology`, 'Technology'),
        right: (
          <Link
            href={`/sneakers/technology/${formatString(midsole)}`}
            prefetch={false}
            data-qa="technology_link"
          >
            {midsole}
          </Link>
        ),
      },
    }),
    ...(category[0] &&
      isCategoryAndFacetMatching && {
        category: {
          left: t(`${TP}.category`, 'Category'),
          right: (
            <Link
              href={`/sneakers/brand/${formatString(brandName)}/${formatString(category[0])}`}
              prefetch={false}
              data-qa="category_link"
            >
              {category[0]}
            </Link>
          ),
        },
      }),
    ...(isSneakers &&
      designer && {
        designer: {
          left: t(`${TP}.designer`, 'Designer'),
          right: (
            <Link
              href={`/sneakers/designer/${formatString(designer)}`}
              prefetch={false}
              data-qa="designer_link"
            >
              {designer}
            </Link>
          ),
        },
      }),
    ...(featuredIn?.length > 0 && {
      related: {
        left: t(`${TP}.related`, 'Related'),
        right: (
          <div className="relatedLinks">
            {featuredIn.slice(0, 3)?.map((featured) => (
              <Link
                href={`/${featured.clickthroughDestination}`}
                prefetch={false}
                data-qa="related"
              >
                {featured.name}
              </Link>
            ))}
          </div>
        ),
      },
    }),
  };

  return (
    <FactsTable.Table data-qa="facts_container">
      <tbody>
        {Object.entries(data).map(([key, { left, right }]) => {
          return (
            <tr key={key}>
              <td className="factsTableKey" data-qa={`${key}_key`}>
                {left}
              </td>
              <td className="factsTableValue" data-qa={`${key}_value`}>
                {right}
              </td>
            </tr>
          );
        })}
      </tbody>
    </FactsTable.Table>
  );
};

FactsTable.Table = styled.table`
  padding: 0 16px;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 12px;
  text-transform: capitalize;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ height: '14px', size: '11.5px' })}
    border-spacing: 0 24px;
    padding: 0 16.5px;
    margin-bottom: 36px;
  }

  a {
    text-decoration: underline;

    :hover {
      text-decoration: underline;
    }
  }

  .factsTableValue {
    text-align: right;
  }

  td {
    vertical-align: top;
  }

  .relatedLinks {
    display: flex;
    flex-direction: column;

    > a:not(:last-child) {
      margin-bottom: 8px;
    }
  }
`;
